import { useEffect, useRef, useState } from "react"
import Api from "../api/api"
import { SkuPromotion } from "../api/models"
import Spinner from "./widgets/Spinner"
import { formatPrice } from "./widgets/UpgradeInfo"
import { backdropDismiss } from "../utils/backdropDismiss"

// A Date as a datetime-local input wants it: local wall-clock, minute
// precision ("2026-11-27T09:00")
function toLocalInputValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

// Add/edit dialog for a sku's promotions (admin SKU page). Conditionally
// mounted by the parent (OfferingDialog pattern); promotion null = create.
// The discount is typed EITHER as a percent off OR as the promoted price —
// the two inputs are linked through the list price of the sku's live
// one-time offering, so typing one fills the other; whichever was typed
// last is what gets stored (the backend derives the rate at read time). A
// running promotion keeps its start (the backend rejects changes); an
// ended one is never offered for editing.
export function PromotionDialog({ skuId, listPriceCents, promotion, accessToken, onClose, onSaved }: {
  skuId: string
  // the live one-time offering's price — null when the sku isn't sold
  // one-off (then only a percent can be set)
  listPriceCents: number | null
  promotion: SkuPromotion | null
  accessToken: string
  onClose: () => void
  onSaved: () => void
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const isEdit = promotion !== null
  const running = promotion?.status === "running"
  const [name, setName] = useState(promotion?.name ?? "")
  const [mode, setMode] = useState<"percent" | "price">(promotion?.priceCents != null ? "price" : "percent")
  const [percentText, setPercentText] = useState(() =>
    promotion?.discountPercent != null ? String(promotion.discountPercent)
      : promotion?.priceCents != null && listPriceCents ? String(Math.round((1 - promotion.priceCents / listPriceCents) * 100))
        : "")
  const [priceText, setPriceText] = useState(() =>
    promotion?.priceCents != null ? (promotion.priceCents / 100).toFixed(2)
      : promotion?.discountPercent != null && listPriceCents ? (listPriceCents * (1 - promotion.discountPercent / 100) / 100).toFixed(2)
        : "")
  const [startsText, setStartsText] = useState(() => toLocalInputValue(promotion ? new Date(promotion.startsAt) : new Date()))
  const [endsText, setEndsText] = useState(() => promotion?.endsAt ? toLocalInputValue(new Date(promotion.endsAt)) : "")
  const [step, setStep] = useState<"form" | "saving" | "error">("form")
  const [errorMessage, setErrorMessage] = useState("")

  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  // typing a percent fills in the price it implies, and vice versa
  const changePercent = (text: string) => {
    setMode("percent")
    setPercentText(text)
    const percent = Number(text)
    setPriceText(text !== "" && listPriceCents && Number.isFinite(percent) ? (listPriceCents * (1 - percent / 100) / 100).toFixed(2) : "")
  }
  const changePrice = (text: string) => {
    setMode("price")
    setPriceText(text)
    const cents = Math.round(Number(text) * 100)
    setPercentText(text !== "" && listPriceCents && Number.isFinite(cents) ? String(Math.round((1 - cents / listPriceCents) * 100)) : "")
  }

  const percent = Number(percentText)
  const priceCents = Math.round(Number(priceText) * 100)
  const startsAt = startsText ? new Date(startsText) : null
  const endsAt = endsText ? new Date(endsText) : null
  const discountValid = mode === "percent"
    ? percentText !== "" && Number.isInteger(percent) && percent >= 1 && percent <= 99
    : priceText !== "" && listPriceCents !== null && Number.isInteger(priceCents) && priceCents > 0 && priceCents < listPriceCents
  const windowValid = startsAt !== null && !isNaN(startsAt.getTime())
    && (endsAt === null || (!isNaN(endsAt.getTime()) && endsAt.getTime() > startsAt.getTime()))
  const canSubmit = discountValid && windowValid && step !== "saving"

  const handleSubmit = async () => {
    if (!canSubmit || startsAt === null) return
    setStep("saving")
    setErrorMessage("")
    const discount = mode === "percent"
      ? { discountPercent: percent, priceCents: null }
      : { discountPercent: null, priceCents }
    try {
      if (promotion === null) {
        await Api.createPromotion({
          skuId,
          promotion: { name: name.trim() || null, ...discount, startsAt: startsAt.toISOString(), endsAt: endsAt?.toISOString() ?? null },
          accessToken
        })
      } else {
        await Api.updatePromotion({
          skuId,
          promotionId: promotion.id,
          patch: {
            name: name.trim() || null,
            ...discount,
            // a running promotion's start is fixed — leave it out
            ...(running ? {} : { startsAt: startsAt.toISOString() }),
            endsAt: endsAt?.toISOString() ?? null
          },
          accessToken
        })
      }
      onSaved()
      dialogRef.current?.close()
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "unknown error")
      setStep("error")
    }
  }

  return (
    <dialog
      ref={dialogRef}
      className="redeem-dialog"
      onClose={onClose}
      {...backdropDismiss(() => { if (step !== "saving") dialogRef.current?.close() })}
    >
      <div className="redeem-dialog-content">
        <button className="redeem-dialog-close" onClick={() => dialogRef.current?.close()} aria-label="Close">&times;</button>
        <h2 className="redeem-dialog-title">{isEdit ? "Edit Promotion" : "Add Promotion"}</h2>

        <div className="generate-codes-form">
          <label className="generate-codes-field">
            <span className="generate-codes-label">Name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="generate-codes-input"
              disabled={step === "saving"}
              placeholder="Sale"
              maxLength={255}
              autoFocus={!isEdit}
            />
            <span className="generate-codes-hint">Shown under the price in the store and the buy widget — optional</span>
          </label>

          <div className="generate-codes-field">
            <span className="generate-codes-label">Discount</span>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <input
                  type="number"
                  min={1}
                  max={99}
                  step={1}
                  value={percentText}
                  onChange={(e) => changePercent(e.target.value)}
                  className="generate-codes-input"
                  disabled={step === "saving"}
                  placeholder="% off"
                />
                <span className="generate-codes-hint">Percent off</span>
              </label>
              <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <input
                  type="number"
                  min={0.01}
                  step={0.01}
                  value={priceText}
                  onChange={(e) => changePrice(e.target.value)}
                  className="generate-codes-input"
                  disabled={step === "saving" || listPriceCents === null}
                  placeholder="$"
                />
                <span className="generate-codes-hint">Promo price ($)</span>
              </label>
            </div>
            <span className="generate-codes-hint">
              {listPriceCents !== null
                ? `List price ${formatPrice(listPriceCents / 100)} — type either and the other follows; the one you typed last is what's stored`
                : "The SKU has no one-time offering yet, so only a percent can be set"}
            </span>
          </div>

          <label className="generate-codes-field">
            <span className="generate-codes-label">Starts</span>
            <input
              type="datetime-local"
              value={startsText}
              onChange={(e) => setStartsText(e.target.value)}
              className="generate-codes-input"
              disabled={step === "saving" || running}
            />
            <span className="generate-codes-hint">
              {running ? "A running promotion's start cannot be changed" : "Local time — a start in the past means right away"}
            </span>
          </label>

          <label className="generate-codes-field">
            <span className="generate-codes-label">Ends</span>
            <input
              type="datetime-local"
              value={endsText}
              onChange={(e) => setEndsText(e.target.value)}
              className="generate-codes-input"
              disabled={step === "saving"}
            />
            <span className="generate-codes-hint">Leave empty for an open-ended promotion — end it from the list when the time comes</span>
          </label>
        </div>

        {step === "error" && errorMessage && (
          <p className="generate-codes-error">{errorMessage}</p>
        )}

        <div className="redeem-actions">
          <button onClick={() => dialogRef.current?.close()} disabled={step === "saving"}>Cancel</button>
          <button className="btn-upgrade" disabled={!canSubmit} onClick={handleSubmit}>
            {step === "saving" ? <><Spinner size={12} /> Saving…</> : isEdit ? "Save" : "Add"}
          </button>
        </div>
      </div>
    </dialog>
  )
}

// Confirmation for the two destructive row actions (DeleteOfferingDialog
// pattern): "end" stops a running promotion now and keeps it as history,
// "delete" drops a scheduled or running one for good.
export function PromotionActionDialog({ skuId, promotion, action, accessToken, onClose, onDone }: {
  skuId: string
  promotion: SkuPromotion
  action: "end" | "delete"
  accessToken: string
  onClose: () => void
  onDone: () => void
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [step, setStep] = useState<"confirm" | "working" | "error">("confirm")
  const [errorMessage, setErrorMessage] = useState("")

  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  const handleConfirm = async () => {
    setStep("working")
    setErrorMessage("")
    try {
      if (action === "end")
        await Api.updatePromotion({ skuId, promotionId: promotion.id, patch: { endsAt: new Date().toISOString() }, accessToken })
      else
        await Api.deletePromotion({ skuId, promotionId: promotion.id, accessToken })
      onDone()
      dialogRef.current?.close()
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "unknown error")
      setStep("error")
    }
  }

  const label = promotion.name ?? "this promotion"
  return (
    <dialog
      ref={dialogRef}
      className="redeem-dialog"
      onClose={onClose}
      {...backdropDismiss(() => { if (step !== "working") dialogRef.current?.close() })}
    >
      <div className="redeem-dialog-content">
        <button className="redeem-dialog-close" onClick={() => dialogRef.current?.close()} aria-label="Close">&times;</button>
        <h2 className="redeem-dialog-title">{action === "end" ? "End Promotion" : "Delete Promotion"}</h2>
        <p>
          {action === "end"
            ? <>End <strong>{label}</strong> now? Prices return to normal within a minute; the promotion stays in the list as history.</>
            : <>Delete <strong>{label}</strong>? {promotion.status === "running" ? "Prices return to normal within a minute and nothing is kept." : "It has not run yet, so nothing else changes."}</>}
        </p>
        {step === "error" && errorMessage && (
          <p className="generate-codes-error">{errorMessage}</p>
        )}
        <div className="redeem-actions">
          <button onClick={() => dialogRef.current?.close()} disabled={step === "working"}>Cancel</button>
          <button className="btn-upgrade" disabled={step === "working"} onClick={handleConfirm}>
            {step === "working" ? <><Spinner size={12} /> {action === "end" ? "Ending…" : "Deleting…"}</> : action === "end" ? "End now" : "Delete"}
          </button>
        </div>
      </div>
    </dialog>
  )
}
